import { cn } from "@wags/ui/ui-utils";
import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown from "react-markdown";

type ListingDescriptionMarkdownProps = {
    children: string;
    className?: string;
};

function paragraphText(props: ComponentPropsWithoutRef<"p">) {
    return (
        <p
            {...props}
            className={cn(
                "m-0 text-body leading-[155%] text-doberman-black/80",
                props.className,
            )}
        />
    );
}

function headingText({
    children,
    className,
    ...props
}: ComponentPropsWithoutRef<"h3">) {
    return (
        <h3
            {...props}
            className={cn(
                "mb-1 mt-5 text-body font-semibold text-doberman-black first:mt-0",
                className,
            )}
        >
            {children}
        </h3>
    );
}

export function ListingDescriptionMarkdown({
    children,
    className,
}: ListingDescriptionMarkdownProps) {
    return (
        <div
            className={cn(
                "max-w-prose break-words whitespace-normal text-doberman-black",
                "[&>p+p]:mt-2.5",
                "[&_a]:font-medium [&_a]:text-doberman-black [&_a]:underline [&_a]:decoration-doberman-black/25 [&_a]:underline-offset-4",
                "[&_strong]:font-medium [&_strong]:text-doberman-black",
                className,
            )}
        >
            <ReactMarkdown
                components={{
                    blockquote: (props) => (
                        <blockquote
                            {...props}
                            className={cn(
                                "my-3 border-l-2 border-doberman-black/15 pl-4 text-doberman-black/70",
                                props.className,
                            )}
                        />
                    ),
                    h1: headingText,
                    h2: headingText,
                    h3: headingText,
                    h4: headingText,
                    h5: headingText,
                    h6: headingText,
                    li: (props) => (
                        <li
                            {...props}
                            className={cn("pl-1", props.className)}
                        />
                    ),
                    ol: (props) => (
                        <ol
                            {...props}
                            className={cn(
                                "my-2 list-decimal space-y-1 pl-5 text-body leading-[155%] text-doberman-black/80",
                                props.className,
                            )}
                        />
                    ),
                    p: paragraphText,
                    ul: (props) => (
                        <ul
                            {...props}
                            className={cn(
                                "my-2 list-disc space-y-1 pl-5 text-body leading-[155%] text-doberman-black/80",
                                props.className,
                            )}
                        />
                    ),
                }}
            >
                {children}
            </ReactMarkdown>
        </div>
    );
}
